import { useFlash } from 'contexts/flash';
import { useCallback, useEffect, useState } from 'react';
import {
  generatePostSeizure,
  getDetailPostSeizure,
  renderPayloadSeizure,
  updateDetailPostSeizure,
  updateStatusTransition
} from 'services/files';
import { useNavigate, useParams } from 'react-router-dom';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { FormPostSeizure } from '../components/modules/FormPostSeizure/FormPostSeizure';
import { isNull, omitBy } from 'lodash';
import axios from 'axios';
import { Lead, TFile } from 'types/user';

export type FilePostSeizureDetail = {
  id: number;
  fileId: string;
  quickNote: string;
  status: 'AWAITING_POI_PAD' | 'COMPLETED' | 'POST_SEIZURE' | string;
  nextPaymentDate?: string | null;
  otherFeesIncurred?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  employer?: string | null;
  employerPhone?: string | null;
  provider?: string | null;
  policy?: string | null;
  expiryDate?: string | null;
  frequency?: string | null;
  createdAt: string;
  bailiffFees?: number;
  escFees?: number;
  auctionHouseFees?: number;
  redemptionFees?: number;
  mvsSearch?: number;
  arrears?: number;
  arrearsTo?: string;
  clientFees?: number;
  transportFees?: number;
  transportFeesText?: string;
  auctionHouseFeesText?: string;
  auctionFees?: number;
  otherFees?: number;
  updatedAt: string;
  installmentAmount?: number;
  file: TFile;
  addressLead: Lead;
  postSeizuresDocuments?: {
    id: string;
    updatedAt: string;
    storageId: string;
    url: string;
    name: string;
  }[];
  preSeizuresDocuments?: {
    id: string;
    updatedAt: string;
    storageId: string;
    url: string;
    name: string;
  }[];
};
export const useEditPostSeizure = () => {
  const { setFlash } = useFlash();
  const navigate = useNavigate();
  const { id } = useParams();
  const [dataDetail, setDataDetail] = useState<FormPostSeizure>();

  const fetchDetail = useCallback(async () => {
    const { filePostSeizure } = await getDetailPostSeizure(Number(id));
    const dataConvert = omitBy(filePostSeizure, isNull) as FilePostSeizureDetail;
    const formValue: FormPostSeizure = {
      fileId: Number(dataConvert.fileId),
      quickNote: dataConvert.quickNote,
      debtorDetail: {
        name: dataConvert.file?.firstName + ' ' + dataConvert.file?.lastName || '',
        address: dataConvert.addressLead?.id?.toString(),
        otherAddress: '',
        phoneNumber: dataConvert.phoneNumber || '',
        email: dataConvert.email || '',
        employer: dataConvert.employer || '',
        employerPhone: dataConvert.employerPhone || '',
        vin: dataConvert.file?.vin,
        fileNumber: dataConvert.file?.fileNumber
      },
      insuranceDetail: {
        provider: dataConvert.provider || '',
        policyNumber: dataConvert.policy || '',
        expiry: dataConvert.expiryDate ? new Date(dataConvert.expiryDate) : null
      },
      costDetail: {
        bailiffFees: dataConvert.bailiffFees,
        redemptionFees: dataConvert.redemptionFees,
        mvsFees: dataConvert.mvsSearch,
        arrears: dataConvert.arrears,
        arrearsTo: dataConvert.arrearsTo,
        clientFees: dataConvert.escFees,
        transportFees: dataConvert.transportFees || dataConvert?.transportFeesText,
        auctionFees: dataConvert.auctionHouseFees || dataConvert?.auctionHouseFeesText,
        otherFeesIncurred: dataConvert.otherFeesIncurred || '',
        otherFees: dataConvert.otherFees
      },
      nextPaymentDetail: {
        nextPaymentDate: dataConvert.nextPaymentDate ? new Date(dataConvert.nextPaymentDate) : null,
        installmentAmount: dataConvert?.installmentAmount || undefined,
        frequency: dataConvert.frequency || ''
      },
      documents: dataConvert.postSeizuresDocuments?.map((doc) => ({
        id: Number(doc.id),
        url: doc.url,
        name: doc.name,
        storageId: Number(doc.storageId)
      })),
      fileDocumentAdd: []
    };
    setDataDetail(formValue);
  }, [id]);

  const { asyncCallback: asyncFetchDetail } = useAsyncCallback(fetchDetail, []);

  useEffect(() => {
    asyncFetchDetail();
  }, [id]);

  const onSubmitUpdatePostSeizure = useCallback(
    async (formData: FormPostSeizure) => {
      const formData2 = new FormData();
      let filePhotosIds: number[] = [];
      const filePhotos = formData.fileDocumentAdd;
      if (filePhotos?.length) {
        for (let x = 0; x < filePhotos.length; x++) {
          formData2.append('files', filePhotos[x]!, filePhotos[x]!.name);
        }
        const { data: resListIdFile } = await axios.post(
          '/storages/post_seizures_document',
          formData2,
          {
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
        filePhotosIds = resListIdFile.files;
        formData2.delete('files');
      }
      const payload = renderPayloadSeizure(formData, [
        ...(formData?.documents?.map((item) => item.storageId) || []),
        ...filePhotosIds
      ]);
      await updateDetailPostSeizure(Number(id), payload);
      await asyncFetchDetail();
      setFlash({ type: 'success', message: 'Update post seizure successful' });
    },
    [asyncFetchDetail, id, setFlash]
  );
  const { asyncCallback: asyncOnSubmitUpdatePostSeizure } = useAsyncCallback(
    onSubmitUpdatePostSeizure,
    []
  );

  const handleMoveToArchive = useCallback(async () => {
    await updateStatusTransition(Number(dataDetail?.fileId), 'CLOSE_REDEMPTION');
    setFlash({ type: 'success', message: 'Move to archive successful' });
    navigate('/admin/control-panel?tab=archive');
  }, [dataDetail?.fileId]);

  const handleGenerate = async (formData: FormPostSeizure) => {
    const formData2 = new FormData();
    let filePhotosIds: number[] = [];
    const filePhotos = formData.fileDocumentAdd;
    if (filePhotos?.length) {
      for (let x = 0; x < filePhotos.length; x++) {
        formData2.append('files', filePhotos[x]!, filePhotos[x]!.name);
      }
      const { data: resListIdFile } = await axios.post(
        '/storages/post_seizures_document',
        formData2,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      filePhotosIds = resListIdFile.files;
      formData2.delete('files');
    }
    const payload = renderPayloadSeizure(formData, [
      ...(formData?.documents?.map((item) => item.storageId) || []),
      ...filePhotosIds
    ]);
    const data = await updateDetailPostSeizure(Number(id), payload);
    if (data.filePostSeizure.id) {
      await generatePostSeizure(data.filePostSeizure.id, payload);
      setFlash({ type: 'success', message: 'Generate successful' });
    }
    await asyncFetchDetail();
  };
  const { asyncCallback: generateSeizure } = useAsyncCallback(handleGenerate, []);

  return {
    onSubmitUpdatePostSeizure: asyncOnSubmitUpdatePostSeizure,
    handleOnBack: () => navigate(`/admin/redemptions`),
    dataDetail,
    generateSeizure,
    handleMoveToArchive
  };
};
